import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Form,
  Message,
  Loader,
  Button,
  FormField,
  Select,
} from 'semantic-ui-react'
import { useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import api from '~/services/api'
import { ContainerSemantic } from './styles'
import { toast } from 'react-toastify'
import { useGlobalContext } from '../../contexts/GlobalContext'

function validarComRegex(valor, regex) {
  const regexReal = RegExp(regex)

  if (typeof valor !== 'string') return false
  if (!(regexReal instanceof RegExp)) return false

  return regexReal.test(valor)
}

function validarCelular(numero) {
  if (!numero) return false

  // Remove tudo que não for número
  const apenasNumeros = numero.replace(/\D/g, '')

  // Deve ter 11 dígitos: 2 do DDD + 9 do número
  if (apenasNumeros.length !== 11) return false

  const regex = /^(\d{2})(9\d{8})$/
  return regex.test(apenasNumeros)
}

function formatarCelular(numero) {
  if (!numero) return ''

  // Remove tudo que não for número
  const numeros = numero.replace(/\D/g, '')

  if (numeros.length !== 11) return numero // Retorna original se não tiver 11 dígitos

  const ddd = numeros.slice(0, 2)
  const parte1 = numeros.slice(2, 7)
  const parte2 = numeros.slice(7)

  return `(${ddd}) ${parte1}-${parte2}`
}

function validarCNPJ(cnpj) {
  if (!cnpj) return false

  // Remove tudo que não for número
  const cnpjLimpo = cnpj.replace(/[^\d]/g, '')

  // CNPJ precisa ter 14 dígitos
  if (cnpjLimpo.length !== 14) return false

  // Elimina CNPJs com todos os dígitos iguais
  if (/^(\d)\1{13}$/.test(cnpjLimpo)) return false

  const calcularDigito = (base) => {
    let soma = 0
    let peso = base.length === 12 ? 5 : 6

    for (let i = 0; i < base.length; i++) {
      soma += Number.parseInt(base[i]) * peso--
      if (peso < 2) peso = 9
    }

    const resto = soma % 11
    return resto < 2 ? '0' : String(11 - resto)
  }

  const base = cnpjLimpo.slice(0, 12)
  const digito1 = calcularDigito(base)
  const digito2 = calcularDigito(base + digito1)

  const cnpjCalculado = base + digito1 + digito2

  return cnpjLimpo === cnpjCalculado
}

const formatarCnpj = (cnpj) => {
  const sanitizedCnpj = cnpj.replace(/[^\d]+/g, '')
  if (sanitizedCnpj.length !== 14) {
    return cnpj
  }
  return sanitizedCnpj.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    '$1.$2.$3/$4-$5',
  )
}

const formatarRg = (rg) => {
  const sanitizedRg = rg.replace(/[^\d]+/g, '')
  if (sanitizedRg.length !== 9) {
    return rg
  }
  return sanitizedRg.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
}

function validarRG(rg) {
  if (!rg) return false

  // Remove tudo que não é número ou letra X
  const rgLimpo = rg.replace(/[^\dXx]/g, '').toUpperCase()

  // Verifica se tem exatamente 9 caracteres
  if (!/^\d{8}[0-9X]$/.test(rgLimpo)) {
    return false
  }

  const numeros = rgLimpo.slice(0, 8).split('').map(Number)
  const verificador = rgLimpo[8]

  // Cálculo do dígito verificador
  let soma = 0
  for (let i = 0; i < 8; i++) {
    soma += numeros[i] * (2 + i) // pesos de 2 a 9
  }

  let digito = 11 - (soma % 11)
  if (digito === 10) digito = 'X'
  else if (digito === 11) digito = '0'
  else digito = String(digito)

  return digito === verificador
}

const formatarCpf = (cpf) => {
  const sanitizedCpf = cpf.replace(/[^\d]+/g, '')
  if (sanitizedCpf.length !== 11) {
    return cpf
  }
  return sanitizedCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

const validarCpf = (cpf) => {
  const sanitizedCpf = cpf.replace(/[^\d]+/g, '')
  if (sanitizedCpf.length !== 11 || /^(\d)\1+$/.test(sanitizedCpf)) {
    return false
  }
  let soma = 0
  let resto
  for (let i = 1; i <= 9; i++) {
    soma += Number.parseInt(sanitizedCpf.substring(i - 1, i), 10) * (11 - i)
  }
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) {
    resto = 0
  }
  if (resto !== Number.parseInt(sanitizedCpf.substring(9, 10), 10)) {
    return false
  }
  soma = 0
  for (let i = 1; i <= 10; i++) {
    soma += Number.parseInt(sanitizedCpf.substring(i - 1, i), 10) * (12 - i)
  }
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) {
    resto = 0
  }
  if (resto !== Number.parseInt(sanitizedCpf.substring(10, 11), 10)) {
    return false
  }
  return true
}

export default function Contato({ match }) {
  const [allFields, setAllFields] = useState([])

  const { globalDispatch } = useGlobalContext()
  const profile = useSelector((state) => state.user.profile)
  const { id } = match.params

  const [contact, setContact] = useState({})
  const [loading, setLoading] = useState(false)
  const [descricao, setDescricao] = useState('')
  const [contactNumbers, setContactNumbers] = useState([])
  const [contactFields, setContactFields] = useState([])
  const [templateFields, setTemplateFields] = useState([])
  const [fraseologia, setFraseologia] = useState('')

  const getAllFields = useCallback(
    async (contactFields) => {
      try {
        const { data } = await api.get(`/templatefields/${profile.id_dominio}`)

        const camposNovos = data
          .map((item) => {
            return {
              nome_campo: item.nome_campo,
              conteudo: '',
            }
          })
          .filter(
            (item) =>
              !contactFields.some(
                (field) => field.nome_campo === item.nome_campo,
              ),
          )
        setContactFields([...camposNovos, ...contactFields])
        // setAllFields(data)
      } catch (error) {
        toast.error('Erro ao carregar os campos do ticket')
      }
    },
    [profile.id_dominio],
  )

  const getTemplateFields = useCallback(async () => {
    try {
      const response = await api.get(`/templatefields/${profile.id_dominio}`)
      setTemplateFields(response.data)
    } catch (error) {
      toast.error(error.response.data.error)
    }
  }, [profile])

  const getContact = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get(`/contacts/${id}`)
      setContact(response.data)
      setDescricao(response.data.descricao)
      setContactNumbers(response.data.ContactNumbers)
      setContactFields(response.data.ContactFields)
      setFraseologia(response.data.fraseologia)
      getAllFields(response.data.ContactFields)
    } catch (error) {
      toast.error(error.response.data.error)
    }
    setLoading(false)
  }, [id, getAllFields])

  useEffect(() => {
    getContact()
  }, [getContact])

  useEffect(() => {
    getTemplateFields()
  }, [getTemplateFields])

  function setValue(value, target) {
    const novoArray = templateFields.map((item) => {
      if (item.nome_campo === target) {
        item.conteudo = value
      }
      return item
    })
    setTemplateFields(novoArray)

    const novoArray2 = contactFields.map((item) => {
      if (item.nome_campo === target) {
        item.conteudo = value
      }
      return item
    })
    setContactFields(novoArray2)
  }

  async function handleAddPhoneNumber() {
    const { value: numero } = await Swal.fire({
      title: 'Adicionar novo número',
      input: 'text',
      inputPlaceholder: 'Digite o número',
    })

    const retorno = contactNumbers.filter((item) => item.numero === numero)
    if (retorno.length > 0) {
      const MySwal = withReactContent(Swal)
      return MySwal.fire({
        icon: 'error',
        title: 'Erro ao adicionar',
        text: 'Este número já existe !',
      })
    }

    if (numero) {
      try {
        await api.post(`/contacts/${id}/phones`, { number: numero })
      } catch (error) {
        toast.error(error.response.data.error)
      }

      getContact()
    }
    return false
  }

  async function handleAddField() {
    const { value: campo } = await Swal.fire({
      title: 'Adicionar novo campo',
      input: 'text',
      inputPlaceholder: 'Digite o nome do campo',
    })

    const retorno = contactFields.filter((item) => item.nome_campo === campo)
    if (retorno.length > 0) {
      const MySwal = withReactContent(Swal)
      return MySwal.fire({
        icon: 'error',
        title: 'Erro ao adicionar',
        text: 'Este campo já existe !',
      })
    }

    if (campo) {
      setContactFields([...contactFields, { nome_campo: campo, conteudo: '' }])
      // dispatch(addCustomParamRequest({ name: campo }))
      // api.post('templatefields', {
      //   name,
      //   id_dominio,
      //   tipo,
      // })
    }
    return false
  }

  async function handleDeleteNumber(data) {
    const MySwal = withReactContent(Swal)
    MySwal.fire({
      title: `Remover numero: ${data.number}`,
      text: 'Tem certeza que deseja deletar ?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (result.value) {
        await api.delete(`/contacts/${id}/phones/${data.id}`)
        getContact()
      }
      return false
    })
  }

  function handleDeleteField(data) {
    const MySwal = withReactContent(Swal)
    MySwal.fire({
      title: `Apagar campo: ${data.name}`,
      text: 'Tem certeza que deseja deletar ?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.value) {
        if (data.id) {
          api.delete(`/contactField/${data.id}`).then((response) => {
            if (response.data.error) {
              toast.error(response.data.error)
            } else {
              toast.success('Campo deletado')
              // getContact()
            }
          })
        }
      }

      setContactFields(
        contactFields.filter((item) => item.nome_campo !== data.name),
      )

      return false
    })
  }

  function handleSubmit() {
    // alert('implementar handleSubmit')
    api
      .put(`/contacts/${id}`, {
        ...contact,
        descricao,
        fraseologia,
        ContactFields: contactFields,
      })
      .then((response) => {
        if (response.data.error) {
          toast.error(response.data.error)
        } else {
          globalDispatch({
            type: 'SET_DATA',
            payload: {
              tickets: [],
            },
          })
          toast.success('Contato atualizado')
        }
      })
      .catch((error) => {
        toast.error(error.response.data.error)
      })
    // dispatch(updateContactRequest({ ...contact, did, descricao, fraseologia }))
  }

  return (
    <ContainerSemantic>
      {!loading ? (
        <Form onSubmit={handleSubmit}>
          <Message>
            <Form.Group widths="equal">
              <Form.Input
                name="descricao"
                fluid
                label="Nome"
                placeholder="Nome do usuário"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </Form.Group>
          </Message>
          {contactNumbers && contactNumbers.length > 0 && (
            <Message>
              <Form.Group className="fields_personalizados">
                {contactNumbers?.map((field) => {
                  return (
                    <Form.Input
                      key={field.numero}
                      name={field.numero}
                      fluid
                      label="numero"
                      placeholder={field.numero}
                      value={field.numero}
                      // onChange={(e) => {
                      //   setValuePhone(e.target.value, field.numero)
                      // }}
                      action={{
                        type: 'button',
                        color: 'red',
                        icon: 'delete',
                        onClick: () => {
                          handleDeleteNumber({
                            id: field.id,
                            number: field.numero,
                          })
                        },
                      }}
                    />
                  )
                })}
              </Form.Group>
            </Message>
          )}
          <Button
            fluid
            positive
            type="button"
            onClick={() => handleAddPhoneNumber()}
            style={{ marginBottom: '10px' }}
          >
            Adicionar telefone
          </Button>
          {contactFields && contactFields.length > 0 && (
            <Message>
              <Form.Group className="fields_personalizados">
                {contactFields?.map((field) => {
                  const findFieldConfig = templateFields.find(
                    (item) => item.nome_campo === field.nome_campo,
                  )

                  console.log(findFieldConfig)

                  if (!findFieldConfig) {
                    return (
                      <Form.Input
                        key={field.id}
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(value, field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }

                  if (findFieldConfig.tipo === 1) {
                    return (
                      <Form.Input
                        key={field.id}
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(value, field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }

                  if (findFieldConfig.tipo === 2) {
                    return (
                      <div className="field field_select" key={field.id}>
                        <FormField
                          className="field_select"
                          control={Select}
                          fluid
                          error={
                            !findFieldConfig.template_fields_values.some(
                              (item) =>
                                item.id === Number.parseInt(field.conteudo, 10),
                            )
                          }
                          label={field.nome_campo}
                          options={[
                            ...findFieldConfig.template_fields_values.map(
                              (item) => {
                                return {
                                  key: item.id,
                                  text: item.valor,
                                  value: item.id,
                                }
                              },
                            ),
                          ]}
                          value={Number.parseInt(field.conteudo, 10)}
                          onChange={(e, { value }) => {
                            setValue(value, field.nome_campo)
                          }}
                          placeholder={'campo de seleção'}
                        />
                      </div>
                    )
                  }

                  if (findFieldConfig.tipo === 3) {
                    return (
                      <Form.Input
                        key={field.id}
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(value.replace(/\D/g, ''), field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }

                  if (findFieldConfig.tipo === 4) {
                    return (
                      <Form.Input
                        key={field.id}
                        error={
                          field.conteudo
                            ? field.conteudo &&
                              field.conteudo.length > 0 &&
                              !validarCpf(field.conteudo)
                            : false
                        }
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(formatarCpf(value), field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }
                  if (findFieldConfig.tipo === 5) {
                    return (
                      <Form.Input
                        key={field.id}
                        error={
                          field.conteudo
                            ? field.conteudo &&
                              field.conteudo.length > 0 &&
                              !validarRG(field.conteudo)
                            : false
                        }
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(formatarRg(value), field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }
                  if (findFieldConfig.tipo === 6) {
                    return (
                      <Form.Input
                        key={field.id}
                        error={
                          field.conteudo
                            ? field.conteudo &&
                              field.conteudo.length > 0 &&
                              !validarCNPJ(field.conteudo)
                            : false
                        }
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(formatarCnpj(value), field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }
                  if (findFieldConfig.tipo === 7) {
                    return (
                      <Form.Input
                        key={field.id}
                        error={
                          field.conteudo
                            ? field.conteudo &&
                              field.conteudo.length > 0 &&
                              !validarCelular(field.conteudo)
                            : false
                        }
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(formatarCelular(value), field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }
                  if (findFieldConfig.tipo === 8) {
                    return (
                      <Form.Input
                        key={field.id}
                        error={
                          field.conteudo
                            ? field.conteudo &&
                              field.conteudo.length > 0 &&
                              !validarComRegex(
                                field.conteudo,
                                findFieldConfig.regex,
                              )
                            : false
                        }
                        name={field.nome_campo}
                        fluid
                        label={field.nome_campo}
                        placeholder={field.nome_campo}
                        value={field.conteudo}
                        onChange={(e, { value }) => {
                          setValue(value, field.nome_campo)
                        }}
                        action={{
                          type: 'button',
                          color: 'red',
                          icon: 'delete',
                          onClick: () => {
                            handleDeleteField({
                              id: field.id,
                              name: field.nome_campo,
                              contact_id: contact.id,
                            })
                          },
                        }}
                      />
                    )
                  }
                })}
              </Form.Group>
            </Message>
          )}
          <Button fluid positive type="button" onClick={() => handleAddField()}>
            Adicionar campo
          </Button>
          <Message>
            <Form.TextArea
              name="fraseologia"
              label="Fraseologia"
              placeholder="Fraseologia de atendimento"
              value={fraseologia}
              onChange={(e) => setFraseologia(e.target.value)}
            />
          </Message>
          <Button fluid primary type="submit">
            Atualizar
          </Button>
        </Form>
      ) : (
        <Loader active />
      )}
    </ContainerSemantic>
  )
}

Contato.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
}
